import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { listEvents, type EventCardData, type EventFilters } from '../../lib/catalog';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort, formatMoneyCompact, formatTime } from '../../lib/format';
import './EventsPage.css';

/**
 * Filters live in the URL, not component state.
 *
 * The engine already filters server-side (city, genre, dateFrom, dateTo,
 * minPrice, maxPrice), so we pass them straight through rather than fetching
 * everything and filtering in the browser — that keeps a large catalogue
 * workable and makes a filtered view shareable and back-button friendly.
 */
function readFilters(params: URLSearchParams): EventFilters {
  const min = params.get('minPrice');
  const max = params.get('maxPrice');
  return {
    city: params.get('city') ?? undefined,
    genre: params.get('genre') ?? undefined,
    dateFrom: params.get('dateFrom') ?? undefined,
    dateTo: params.get('dateTo') ?? undefined,
    minPrice: min ? Number(min) : undefined,
    maxPrice: max ? Number(max) : undefined,
  };
}

/** Dollars in the form, cents on the wire — the engine speaks cents only. */
const toCents = (dollars: string): number | undefined => {
  const value = Number(dollars);
  return dollars.trim() !== '' && Number.isFinite(value) && value >= 0
    ? Math.round(value * 100)
    : undefined;
};
const toDollars = (cents: number | undefined): string =>
  cents == null ? '' : String(cents / 100);

function EventCard({ event }: { event: EventCardData }) {
  const location = [event.venueName, event.city].filter(Boolean).join(' · ');
  const time = formatTime(event.time);

  return (
    <Link to={`/events/${event.id}`} className="event-card glass-card glass-card--grid">
      <div className="event-card__media">
        {event.featuredImageUrl ? (
          <img src={event.featuredImageUrl} alt="" loading="lazy" />
        ) : (
          <div className="event-card__media-fallback" aria-hidden="true">
            <span className="display">{event.title.slice(0, 1)}</span>
          </div>
        )}
        {event.soldOut && <span className="event-card__flag">Sold out</span>}
        {event.genre && !event.soldOut && (
          <span className="event-card__genre">{event.genre}</span>
        )}
      </div>

      <div className="event-card__body">
        <p className="event-card__date mono">
          {formatDateShort(event.date)}
          {time ? ` · ${time}` : ''}
        </p>
        <h3 className="event-card__title">{event.title}</h3>
        {location && <p className="event-card__venue">{location}</p>}

        <div className="event-card__footer">
          <div className="event-card__price">
            {event.fromPriceCents == null ? (
              <span className="event-card__price-value">Tickets TBA</span>
            ) : (
              <>
                <span className="event-card__price-label">From</span>
                <span className="event-card__price-value mono">
                  {formatMoneyCompact(event.fromPriceCents)}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

export function EventsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  const [events, setEvents] = useState<EventCardData[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Local form state, committed to the URL on submit.
  const [draft, setDraft] = useState({
    city: filters.city ?? '',
    genre: filters.genre ?? '',
    dateFrom: filters.dateFrom ?? '',
    dateTo: filters.dateTo ?? '',
    minPrice: toDollars(filters.minPrice),
    maxPrice: toDollars(filters.maxPrice),
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listEvents(filters)
      .then((result) => {
        if (!cancelled) setEvents(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load events.');
        setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters]);

  const applyFilters = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const next = new URLSearchParams();
      if (draft.city.trim()) next.set('city', draft.city.trim());
      if (draft.genre.trim()) next.set('genre', draft.genre.trim());
      if (draft.dateFrom) next.set('dateFrom', draft.dateFrom);
      if (draft.dateTo) next.set('dateTo', draft.dateTo);
      const min = toCents(draft.minPrice);
      const max = toCents(draft.maxPrice);
      if (min != null) next.set('minPrice', String(min));
      if (max != null) next.set('maxPrice', String(max));
      setSearchParams(next);
    },
    [draft, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setDraft({ city: '', genre: '', dateFrom: '', dateTo: '', minPrice: '', maxPrice: '' });
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const hasFilters = searchParams.toString() !== '';

  return (
    <div className="events-page">
      <header className="events-hero">
        <p className="events-hero__eyebrow">Live events</p>
        <h1 className="events-hero__title display">What&apos;s on sale</h1>
        <p className="events-hero__lede">
          All-inclusive pricing. What you see is what you pay, with no surprise fees at
          checkout.
        </p>
      </header>

      <form className="events-filters" onSubmit={applyFilters}>
        <div className="events-filters__grid">
          <label className="field">
            <span className="field__label">City</span>
            <input
              className="field__input"
              type="text"
              value={draft.city}
              placeholder="Denver"
              onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))}
            />
          </label>

          <label className="field">
            <span className="field__label">Genre</span>
            <input
              className="field__input"
              type="text"
              value={draft.genre}
              placeholder="Techno"
              onChange={(e) => setDraft((d) => ({ ...d, genre: e.target.value }))}
            />
          </label>

          <label className="field">
            <span className="field__label">From date</span>
            <input
              className="field__input"
              type="date"
              value={draft.dateFrom}
              onChange={(e) => setDraft((d) => ({ ...d, dateFrom: e.target.value }))}
            />
          </label>

          <label className="field">
            <span className="field__label">To date</span>
            <input
              className="field__input"
              type="date"
              value={draft.dateTo}
              onChange={(e) => setDraft((d) => ({ ...d, dateTo: e.target.value }))}
            />
          </label>

          <label className="field">
            <span className="field__label">Min price</span>
            <input
              className="field__input mono"
              type="number"
              min="0"
              step="1"
              value={draft.minPrice}
              placeholder="0"
              onChange={(e) => setDraft((d) => ({ ...d, minPrice: e.target.value }))}
            />
          </label>

          <label className="field">
            <span className="field__label">Max price</span>
            <input
              className="field__input mono"
              type="number"
              min="0"
              step="1"
              value={draft.maxPrice}
              placeholder="250"
              onChange={(e) => setDraft((d) => ({ ...d, maxPrice: e.target.value }))}
            />
          </label>
        </div>

        <div className="events-filters__actions">
          <button type="submit" className="button button--primary">
            Apply filters
          </button>
          {hasFilters && (
            <button type="button" className="button button--ghost" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>
      </form>

      {loading && <p className="events-status">Loading events…</p>}

      {!loading && error && (
        <p className="events-status events-status--error" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && events && events.length === 0 && (
        <div className="events-empty">
          {/* "No events match" implies a filter, which is wrong when none is applied.
              With the catalogue empty by default that was the case a visitor would
              actually hit, so the heading follows the body's existing split. */}
          <h2>{hasFilters ? 'No events match' : 'No events on sale yet'}</h2>
          <p>
            {hasFilters
              ? 'Try widening the date range or clearing a filter.'
              : 'Nothing is on sale right now. Check back soon, or follow an artist to hear when they announce a date.'}
          </p>
          {hasFilters && (
            <button type="button" className="button button--secondary" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      )}

      {!loading && events && events.length > 0 && (
        <>
          <p className="events-count">
            <span className="mono">{events.length}</span>{' '}
            {events.length === 1 ? 'event' : 'events'}
          </p>
          <div className="events-grid">
            {events.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
